import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import MediaLightbox from "@/components/site/MediaLightbox";

const EASE = [0.16, 1, 0.3, 1];

const UGC_VIDEOS = [
  {
    id: "deus-machina",
    label: "Deus Machina",
    src: "/assets/ugc-deus-machina.mp4",
    poster: "/assets/ugc-deus-machina-poster.webp",
  },
  {
    id: "riman-calming-gel",
    label: "RIMAN SKINCARE",
    src: "/assets/ugc-riman-calming-gel.mp4",
    poster: "/assets/ugc-riman-calming-gel-poster.webp",
  },
  {
    id: "riman-powder-wash",
    label: "RIMAN SKINCARE",
    src: "/assets/ugc-riman-powder-wash.mp4",
    poster: "/assets/ugc-riman-powder-wash-poster.webp",
  },
  {
    id: "sauce-piquante",
    label: "Blair's Death",
    src: "/assets/ugc-sauce-piquante.mp4",
    poster: "/assets/ugc-sauce-piquante-poster.webp",
  },
];

const PRODUCT_IMAGES = [
  {
    id: "01",
    alt: "Dermatology Cream — AI product campaign, visual 01",
    src: "/assets/dermatology-cream-1.png",
    thumb: "/assets/dermatology-cream-1.webp",
  },
  {
    id: "02",
    alt: "Dermatology Cream — AI product campaign, visual 02",
    src: "/assets/dermatology-cream-2.png",
    thumb: "/assets/dermatology-cream-2.webp",
  },
  {
    id: "03",
    alt: "Dermatology Cream — AI product campaign, visual 03",
    src: "/assets/dermatology-cream-3.png",
    thumb: "/assets/dermatology-cream-3.webp",
  },
  {
    id: "04",
    alt: "Dermatology Cream — AI product campaign, visual 04",
    src: "/assets/dermatology-cream-4.png",
    thumb: "/assets/dermatology-cream-4.webp",
  },
];

const BRAND_WORLD_MEDIA = [
  { id: "01", type: "image", src: "/assets/coco-paris-01.webp" },
  { id: "10", type: "video", src: "/assets/coco-paris-10.mp4", poster: "/assets/coco-paris-10-poster.webp" },
  { id: "02", type: "image", src: "/assets/coco-paris-02.webp" },
  { id: "03", type: "image", src: "/assets/coco-paris-03.webp" },
  { id: "04", type: "image", src: "/assets/coco-paris-04.webp" },
  { id: "05", type: "image", src: "/assets/coco-paris-05.webp" },
  { id: "06", type: "image", src: "/assets/coco-paris-06.webp" },
  { id: "11", type: "video", src: "/assets/coco-paris-11.mp4", poster: "/assets/coco-paris-11-poster.webp" },
  { id: "07", type: "image", src: "/assets/coco-paris-07.webp" },
  { id: "08", type: "image", src: "/assets/coco-paris-08.webp" },
  { id: "12", type: "video", src: "/assets/coco-paris-12.mp4", poster: "/assets/coco-paris-12-poster.webp" },
  { id: "09", type: "image", src: "/assets/coco-paris-09.webp" },
];

const HATCH_STYLE = {
  backgroundImage:
    "linear-gradient(rgba(0,0,0,0.16), rgba(0,0,0,0.16)), url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Cpath d='M-0.5 0.5l1-1M0 4l4-4M3.5 4.5l1-1' stroke='rgb(42,42,52)' stroke-opacity='0.72' stroke-width='0.5'/%3E%3C/svg%3E\")",
  backgroundRepeat: "repeat",
};

const SubHead = ({ num, label, legend }) => (
  <div className="mb-10 flex flex-wrap items-baseline justify-between gap-x-8 gap-y-3 border-t border-[#27272A] pt-6">
    <p className="font-monoalt text-[11px] uppercase tracking-[0.25em] text-zinc-400">
      ({num}) {label}
    </p>
    {legend && (
      <p className="font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
        {legend}
      </p>
    )}
  </div>
);

export default function AIStudio() {
  const [activeMedia, setActiveMedia] = useState(null);

  return (
    <section id="studio" data-testid="section-ai-studio" className="px-6 py-28 sm:px-10 sm:py-36 lg:px-14">
      <p className="mb-6 font-monoalt text-[13px] font-light uppercase tracking-[0.18em] text-zinc-500">
        // Portfolio
      </p>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
        className="mb-28 max-w-xl"
      >
        <h2 className="font-display text-2xl font-bold uppercase leading-[0.95] tracking-tight sm:text-3xl">
          The lab is live.
        </h2>
        <p className="mt-5 max-w-sm font-monoalt text-[13px] font-normal leading-[2.1] tracking-[0.06em] text-[#8E97A6]">
          AI-powered UGC, product visuals and brand worlds built for social &amp; paid media.
        </p>
      </motion.div>

      <div data-testid="studio-ugc">
        <SubHead num="01" label="AI UGC & Creators" />
        <div className="grid w-full max-w-[558px] grid-cols-3 gap-x-[9px] gap-y-6 md:max-w-none md:grid-cols-4 lg:w-[727px]">
          {UGC_VIDEOS.map((v, i) => (
            <motion.figure
              key={v.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
            >
              <button
                type="button"
                onClick={() => setActiveMedia({ type: "video", ...v })}
                data-testid={`studio-video-${v.id}`}
                aria-label={`Play ${v.label}`}
                className="group peer relative block aspect-[9/16] w-full cursor-pointer overflow-hidden border border-[#27272A] bg-[#121214] transition-colors duration-500 hover:border-white/40"
              >
                <img
                  src={v.poster}
                  alt={v.label}
                  loading="lazy"
                  decoding="async"
                  data-pin-no-hover="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span
                  aria-hidden="true"
                  style={HATCH_STYLE}
                  className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-out group-hover:opacity-0"
                />
                <span className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-black/40 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-white group-hover:bg-white group-hover:text-black sm:bottom-3 sm:right-3 sm:h-9 sm:w-9 lg:bottom-5 lg:right-5 lg:h-11 lg:w-11">
                  <Play className="ml-px h-2.5 w-2.5 fill-current sm:ml-0.5 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4" />
                </span>
              </button>
              <figcaption className="mt-3 font-monoalt text-[9px] uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-500 peer-hover:text-white sm:text-[10px]">
                {v.label}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>

      <div className="mt-24 sm:mt-32" data-testid="studio-product-visuals">
        <SubHead num="02" label="Product Visuals" />
        <div className="group/pv">
          <div className="grid w-full max-w-[558px] grid-cols-4 gap-x-[9px] gap-y-6 md:max-w-none md:flex md:flex-wrap">
          {PRODUCT_IMAGES.map((img, i) => (
            <motion.div
              key={img.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: EASE }}
            >
              <button
                type="button"
                onClick={() =>
                  setActiveMedia({ type: "image", src: img.src, alt: img.alt, label: "Dermatology Cream" })
                }
                data-testid={`studio-product-image-${img.id}`}
                aria-label={`View ${img.alt}`}
                className="group relative block aspect-square w-full cursor-pointer overflow-hidden border border-[#27272A] bg-[#121214] transition-colors duration-500 hover:border-white/40 md:w-[175px]"
              >
                <img
                  src={img.thumb}
                  alt={img.alt}
                  loading="lazy"
                  decoding="async"
                  data-pin-no-hover="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span
                  aria-hidden="true"
                  style={HATCH_STYLE}
                  className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-out group-hover:opacity-0"
                />
                <span className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/30 bg-black/40 opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:scale-110 group-hover:border-white group-hover:bg-white group-hover:text-black group-hover:opacity-100 sm:bottom-3 sm:right-3 sm:h-9 sm:w-9 lg:bottom-5 lg:right-5 lg:h-11 lg:w-11">
                  <ArrowUpRight className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4" />
                </span>
              </button>
            </motion.div>
          ))}
          </div>
          <p className="mt-5 font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400 transition-colors duration-500 group-hover/pv:text-white">
            Dermatology Cream — AI Product Campaign
          </p>
        </div>
      </div>

      <div className="mt-24 sm:mt-32" data-testid="studio-brand-world">
        <SubHead num="03" label="Brand World" />
        <p data-testid="brand-world-client" className="font-monoalt text-[10px] tracking-[0.3em] text-zinc-400">
          e.COCO Paris
        </p>
        <p data-testid="brand-world-type" className="mt-2 font-monoalt text-[10px] uppercase tracking-[0.3em] text-zinc-400">
          E-shop Skincare
        </p>
        <div
          className="mt-8 grid w-full max-w-[558px] grid-cols-4 gap-x-[9px] gap-y-6 md:max-w-none md:grid-cols-[repeat(4,175px)]"
          data-testid="brand-world-grid"
        >
          {BRAND_WORLD_MEDIA.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: EASE }}
              data-testid={`brand-world-media-${m.id}`}
              className="group relative aspect-video w-full overflow-hidden"
            >
              {m.type === "video" ? (
                <video
                  src={m.src}
                  poster={m.poster}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-label={`e.COCO Paris — brand world, film ${m.id}`}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <img
                  src={m.src}
                  alt={`e.COCO Paris — brand world, visual ${m.id}`}
                  loading="lazy"
                  decoding="async"
                  data-pin-no-hover="true"
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <span
                aria-hidden="true"
                style={HATCH_STYLE}
                className="pointer-events-none absolute inset-0 transition-opacity duration-500 ease-out group-hover:opacity-0"
              />
            </motion.div>
          ))}
        </div>
      </div>

      <MediaLightbox media={activeMedia} onClose={() => setActiveMedia(null)} />
    </section>
  );
}
